import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Users, Target, TrendingUp, DollarSign, UserCheck, 
  Calendar, ArrowUpRight, ArrowDownRight, Activity,
  RefreshCw, Clock
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { StatsCard } from '@/components/ui/StatsCard';
import { ChartCard } from '@/components/ui/ChartCard';
import { DashboardFilters } from '@/components/dashboard/DashboardFilters';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const API_URL = import.meta.env.VITE_API_BASE_URL || '';

interface DashboardFilters {
  dates: {
    from: string | null;
    to: string | null;
  };
  tags: string[];
  category: string | null;
}

interface MVPMetricas {
  visitas: number;
  contactos: number;
  tasaConversion: {
    porcentaje: number;
    descripcion: string;
  };
  ingresos: {
    estimadoMensual: number;
    estimadoAnual: number;
  };
}

interface MVP {
  mvpId: string;
  name: string;
  category: string;
  tags: string[];
  metricas: MVPMetricas;
  ultimaActividad: string | null;
}

interface MetricaMensual {
  mes: string;
  metricas: {
    visitas: number;
    contactos: number;
  };
}

interface DashboardResponse {
  status: string;
  timestamp: string;
  filtros: DashboardFilters;
  resumen: {
    totalMVPs: number;
    metricas: {
      visitas: number;
      contactos: number;
      tasaConversionPromedio: {
        porcentaje: number;
        descripcion: string;
      };
      ingresos: {
        estimadoMensual: number;
        estimadoAnual: number;
      };
    };
  };
  topMVPs: {
    porVisitas: MVP[];
    porContactos: MVP[];
    porTasaConversion: MVP[];
    porIngresos: MVP[];
  };
  tendenciaMensual: MetricaMensual[];
  listaMVPs: MVP[];
}

export default function Dashboard() {
  const [filters, setFilters] = useState<DashboardFilters>({
    dates: { from: null, to: null },
    tags: [],
    category: null,
  });
  const [nextUpdate, setNextUpdate] = useState<number>(300); // 5 minutos en segundos
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  // Función para formatear el tiempo restante
  const formatTimeRemaining = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Timer para la siguiente actualización
  useEffect(() => {
    const timer = setInterval(() => {
      setNextUpdate((prev) => {
        if (prev <= 1) {
          return 300; // Reiniciar a 5 minutos
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const { data, isLoading, isError, refetch } = useQuery<DashboardResponse>({
    queryKey: ['dashboard-summary', filters],
    queryFn: async () => {
      const token = sessionStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filters.dates.from) {
        try {
          const formattedDate = format(parseISO(filters.dates.from), 'yyyy-MM-dd');
          params.append('from', formattedDate);
        } catch (error) {
          console.error('Error formatting from date:', error);
        }
      }
      
      if (filters.dates.to) {
        try {
          const formattedDate = format(parseISO(filters.dates.to), 'yyyy-MM-dd');
          params.append('to', formattedDate);
        } catch (error) {
          console.error('Error formatting to date:', error);
        }
      }

      if (filters.category) {
        params.append('category', filters.category);
      }

      if (filters.tags && filters.tags.length > 0) {
        params.append('tags', filters.tags.join(','));
      }

      const queryString = params.toString();
      const url = `${API_URL}/dashboard/summary${queryString ? `?${queryString}` : ''}`;

      console.log('Fetching dashboard with URL:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Error loading dashboard summary');
      }

      const responseData = await response.json();
      setLastUpdate(new Date());
      setNextUpdate(300); // Reiniciar el timer
      console.log('Dashboard response:', responseData);
      return responseData;
    },
    refetchInterval: 300000, // Refetch cada 5 minutos
    refetchOnWindowFocus: false,
  });

  const handleManualRefresh = async () => {
    await refetch();
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-sm text-gray-500">Vista general de tus MVPs y métricas</p>
            </div>
            <div className="flex items-center gap-4">
              {/* Información de actualización */}
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>
                  Última actualización: {formatDistanceToNow(lastUpdate, { addSuffix: true, locale: es })}
                </span>
                <span className="text-primary-600">
                  Próxima: {formatTimeRemaining(nextUpdate)}
                </span>
              </div>
              
              {/* Botón de recarga manual */}
              <button
                onClick={handleManualRefresh}
                disabled={isLoading}
                className={`
                  inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
                  ${isLoading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                  }
                  transition-colors
                `}
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                {isLoading ? 'Actualizando...' : 'Actualizar'}
              </button>
              
              <DashboardFilters onFilterChange={setFilters} isLoading={true} />
            </div>
          </div>
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-500">Cargando estadísticas...</p>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isError) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-sm text-gray-500">Vista general de tus MVPs y métricas</p>
            </div>
            <div className="flex items-center gap-4">
              {/* Información de actualización */}
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>
                  Última actualización: {formatDistanceToNow(lastUpdate, { addSuffix: true, locale: es })}
                </span>
                <span className="text-primary-600">
                  Próxima: {formatTimeRemaining(nextUpdate)}
                </span>
              </div>
              
              {/* Botón de recarga manual */}
              <button
                onClick={handleManualRefresh}
                disabled={isLoading}
                className={`
                  inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
                  ${isLoading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                  }
                  transition-colors
                `}
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                {isLoading ? 'Actualizando...' : 'Actualizar'}
              </button>
              
              <DashboardFilters onFilterChange={setFilters} isLoading={false} />
            </div>
          </div>
          <div className="flex items-center justify-center h-64">
            <div className="text-center text-red-600">
              <p>Error al cargar las estadísticas del dashboard</p>
              <button 
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
              >
                Reintentar
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const { resumen, topMVPs } = data || {
    resumen: {
      totalMVPs: 0,
      metricas: {
        visitas: 0,
        contactos: 0,
        tasaConversionPromedio: {
          porcentaje: 0,
          descripcion: "Sin datos"
        },
        ingresos: {
          estimadoMensual: 0,
          estimadoAnual: 0
        }
      }
    },
    topMVPs: {
      porVisitas: [],
      porContactos: [],
      porTasaConversion: [],
      porIngresos: []
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-sm text-gray-500">Vista general de tus MVPs y métricas</p>
          </div>
          <div className="flex items-center gap-4">
            {/* Información de actualización */}
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>
                Última actualización: {formatDistanceToNow(lastUpdate, { addSuffix: true, locale: es })}
              </span>
              <span className="text-primary-600">
                Próxima: {formatTimeRemaining(nextUpdate)}
              </span>
            </div>
            
            {/* Botón de recarga manual */}
            <button
              onClick={handleManualRefresh}
              disabled={isLoading}
              className={`
                inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
                ${isLoading
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }
                transition-colors
              `}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              {isLoading ? 'Actualizando...' : 'Actualizar'}
            </button>
            
            <DashboardFilters onFilterChange={setFilters} isLoading={isLoading} />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatsCard
            title="Total Visitas"
            value={resumen.metricas.visitas.toLocaleString()}
            description="Número total de visitas a todos los MVPs"
            icon={<Users className="w-6 h-6 text-primary-600" />}
          />
          
          <StatsCard
            title="Total Contactos"
            value={resumen.metricas.contactos.toLocaleString()}
            description="Número total de contactos generados"
            icon={<UserCheck className="w-6 h-6 text-primary-600" />}
          />

          <StatsCard
            title="Tasa de Conversión"
            value={`${resumen.metricas.tasaConversionPromedio.porcentaje}%`}
            description={resumen.metricas.tasaConversionPromedio.descripcion}
            icon={<TrendingUp className="w-6 h-6 text-primary-600" />}
          />

          <StatsCard
            title="Ingresos Estimados"
            value={`$${resumen.metricas.ingresos.estimadoMensual.toLocaleString()}`}
            description={`$${resumen.metricas.ingresos.estimadoAnual.toLocaleString()} anuales estimados`}
            icon={<DollarSign className="w-6 h-6 text-primary-600" />}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard
            title="Top MVPs por Visitas"
            description="MVPs con mayor número de visitas"
            maxHeight="400px"
          >
            <div className="divide-y divide-gray-100">
              {topMVPs.porVisitas.map(mvp => (
                <div key={mvp.mvpId} className="flex items-center justify-between py-3 px-2 hover:bg-gray-50 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 truncate">{mvp.name}</div>
                    <div className="text-sm text-gray-500 truncate">{mvp.category}</div>
                  </div>
                  <div className="text-right ml-4">
                    <div className="font-medium text-gray-900">{mvp.metricas.visitas.toLocaleString()}</div>
                    <div className="text-sm text-gray-500">visitas</div>
                  </div>
                </div>
              ))}
            </div>
          </ChartCard>

          <ChartCard
            title="Top MVPs por Contactos"
            description="MVPs con mayor número de contactos"
            maxHeight="400px"
          >
            <div className="divide-y divide-gray-100">
              {topMVPs.porContactos.map(mvp => (
                <div key={mvp.mvpId} className="flex items-center justify-between py-3 px-2 hover:bg-gray-50 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 truncate">{mvp.name}</div>
                    <div className="text-sm text-gray-500 truncate">{mvp.category}</div>
                  </div>
                  <div className="text-right ml-4">
                    <div className="font-medium text-gray-900">{mvp.metricas.contactos.toLocaleString()}</div>
                    <div className="text-sm text-gray-500">contactos</div>
                  </div>
                </div>
              ))}
            </div>
          </ChartCard>
          
          <ChartCard
            title="Top MVPs por Conversión"
            description="MVPs con mejor tasa de conversión"
            maxHeight="400px"
          >
            <div className="divide-y divide-gray-100">
              {topMVPs.porTasaConversion.map(mvp => (
                <div key={mvp.mvpId} className="flex items-center justify-between py-3 px-2 hover:bg-gray-50 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 truncate">{mvp.name}</div>
                    <div className="text-sm text-gray-500 truncate">{mvp.metricas.tasaConversion.descripcion}</div>
                  </div>
                  <div className="text-right ml-4">
                    <div className="font-medium text-gray-900">{mvp.metricas.tasaConversion.porcentaje}%</div>
                    <div className="text-sm text-gray-500">conversión</div>
                  </div>
                </div>
              ))}
            </div>
          </ChartCard>

          <ChartCard
            title="Top MVPs por Ingresos"
            description="MVPs con mayores ingresos estimados"
            maxHeight="400px"
          >
            <div className="divide-y divide-gray-100">
              {topMVPs.porIngresos.map(mvp => (
                <div key={mvp.mvpId} className="flex items-center justify-between py-3 px-2 hover:bg-gray-50 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 truncate">{mvp.name}</div>
                    <div className="text-sm text-gray-500 truncate">{mvp.category}</div>
                  </div>
                  <div className="text-right ml-4">
                    <div className="font-medium text-gray-900">${mvp.metricas.ingresos.estimadoMensual.toLocaleString()}</div>
                    <div className="text-sm text-gray-500">${mvp.metricas.ingresos.estimadoAnual.toLocaleString()} anuales</div>
                  </div>
                </div>
              ))}
            </div>
          </ChartCard>
        </div>
      </div>
    </DashboardLayout>
  );
} 